/**
 * ClientManagement — onboarding, schedule administration and collector fleet health.
 *
 * What this file used to do
 * ------------------------
 * It shipped a named person as the default `assigned_employee` and sent that
 * string to the backend on every onboarding. It rendered a "Simulate Agent
 * Telemetry Heartbeat" button that posted invented CPU/memory/disk values —
 * under a hardcoded agent token and a real internal AWS hostname — into the same
 * table that feeds client-facing reports. It printed two different install
 * commands, both carrying an agent token that belonged to no client, so a copied
 * command could never have worked. It labelled every unlinked agent with a real
 * customer's name, rendered a missing IP as `127.0.0.1` and a missing heartbeat
 * as "Just now", so an agent that had never reported looked perfectly healthy.
 *
 * All of that is gone. Every value on screen now comes from the API, and where
 * the API has nothing to report this says so explicitly rather than inventing a
 * plausible-looking substitute.
 *
 * Design notes
 * ------------
 * *Install commands.* `GET /clients/{id}/agent-command` is the only source. It
 * returns the client's real token and the server's configured public base URL,
 * and 503s when `PUBLIC_BASE_URL` is unset. A 503 is surfaced verbatim; no
 * command is synthesised from `window.location`, because a command built from
 * the browser's own address is wrong for every remote host that has to run it.
 *
 * *Agent freshness.* The API classifies each agent as `online`, `stale` or
 * `unknown` and supplies `heartbeat_age_seconds` plus a fleet-wide
 * `stale_after_seconds`. The three states render distinctly — `unknown` in
 * particular is styled as a fault, not as a success.
 *
 * *Validation.* The create endpoint validates strictly and rejects with a 400.
 * The same rules are mirrored here so an operator gets the message next to the
 * offending field, but the server remains the authority: its `detail` is
 * rendered whenever it still refuses.
 *
 * *Permissions.* `can('manage_clients')` and `can('delete_clients')` gate which
 * controls render. This is presentation only — every one of these routes is
 * enforced server-side and a hidden button is not a security boundary.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  Users, Plus, Trash2, Key, Shield, RefreshCw, CheckCircle2, AlertTriangle,
  Server, Loader, XCircle, Terminal, Clock, Mail, Check, Copy, Zap,
  Download, ExternalLink, Eye, EyeOff, FileText, Sliders, Globe, Lock,
} from 'lucide-react'
import {
  getClients,
  getAgents,
  createClient,
  updateClient,
  deleteClient,
  discoverResources,
  testConnection,
  getAgentInstallCommand,
  getCloudFormationTemplate,
  getClientWhitelist,
  updateClientWhitelist,
  getMyIp,
  getNginxWhitelistSnippet,
} from '../api'
import Modal from './Modal'
import { useAuth } from '../context/AuthContext'

// ── Client-side mirrors of the server's validators ───────────────────────────
// Kept deliberately identical to server/security.py and server/emailer.py so the
// message an operator sees is the message the server would have produced.

const ACCOUNT_ID_RE = /^\d{12}$/
const REGION_RE = /^[a-z]{2}(?:-[a-z]+)+-\d$/
const ROLE_ARN_RE = /^arn:aws(?:-cn|-us-gov)?:iam::\d{12}:role\/[\w+=,.@/-]{1,512}$/
const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/
const EMAIL_RE =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/

const CONSOLE_HOST_SUFFIXES = [
  'signin.aws.amazon.com',
  'console.aws.amazon.com',
  'portal.azure.com',
  'console.cloud.google.com',
]

/** Empty is allowed: `console_url` is optional unless the mode needs it. */
function isValidConsoleUrl(url) {
  const candidate = (url || '').trim()
  if (!candidate) return true
  let parsed
  try {
    parsed = new URL(candidate)
  } catch {
    return false
  }
  if (parsed.protocol !== 'https:' || !parsed.hostname) return false
  if (parsed.username || parsed.password || parsed.port) return false
  const host = parsed.hostname.toLowerCase().replace(/\.$/, '')
  return CONSOLE_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`))
}

/** Commas, semicolons and whitespace all separate addresses, as on the server. */
function splitRecipients(raw) {
  if (!raw) return []
  return raw
    .split(/[,;\s]+/)
    .map((token) => token.trim().replace(/^<|>$/g, '').trim())
    .filter(Boolean)
}

const isValidAddress = (address) => {
  const candidate = (address || '').trim()
  return candidate.length > 0 && candidate.length <= 254 && EMAIL_RE.test(candidate)
}

const validRecipients = (raw) => splitRecipients(raw).filter(isValidAddress)
const invalidRecipients = (raw) => splitRecipients(raw).filter((t) => !isValidAddress(t))

// ── Formatting ───────────────────────────────────────────────────────────────

/**
 * Heartbeat age as relative time. `null` in, `null` out — a missing age must not
 * be coerced into a number, because "0 sec ago" reads as a healthy agent.
 */
function formatRelativeAge(seconds) {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return null
  const total = Math.max(0, Math.round(Number(seconds)))
  if (total < 60) return `${total} sec ago`
  const minutes = Math.floor(total / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hr ago`
  const days = Math.floor(hours / 24)
  return days === 1 ? '1 day ago' : `${days} days ago`
}

function formatThreshold(seconds) {
  const value = Number(seconds)
  if (!Number.isFinite(value) || value <= 0) return null
  if (value < 120) return `${Math.round(value)} seconds`
  const minutes = Math.round(value / 60)
  return minutes < 120 ? `${minutes} minutes` : `${Math.round(minutes / 60)} hours`
}

/** Presentation for each of the three freshness states the API reports. */
const AGENT_STATES = {
  online: { label: 'ONLINE', badge: 'badge-ok', dot: '#10B981', hollow: false },
  stale: { label: 'STALE', badge: 'badge-warning', dot: '#D97706', hollow: false },
  unknown: { label: 'NO HEARTBEAT', badge: 'badge-alarm', dot: 'transparent', hollow: true },
}

const agentState = (status) => AGENT_STATES[status] || AGENT_STATES.unknown

const ACCESS_MODES = [
  { id: 'iam_role', label: '1-Click IAM Role', desc: 'Keyless cross-account, no stored secrets' },
  { id: 'agent', label: 'Linux Agent', desc: 'One-line collector install' },
  { id: 'api', label: 'IAM API Keys', desc: 'Access key ID and secret' },
  { id: 'console_ui', label: 'Console UI', desc: 'Browser sign-in, manual capture' },
]

const ACCESS_MODE_LABELS = {
  iam_role: '1-Click IAM Role',
  agent: 'Linux Agent',
  api: 'IAM API Keys',
  console_ui: 'Console UI',
}

const TAB_ORDER = ['clients', 'agents']

const EMPTY_FORM = {
  client_name: '',
  account_id: '',
  cloud_provider: 'AWS',
  aws_region: 'ap-south-1',
  access_type: 'iam_role',
  console_url: '',
  iam_username: '',
  iam_access_key: '',
  iam_secret_key: '',
  report_time: '11:00',
  report_enabled: false,
  recipient_emails: '',
  assigned_employee: '',
}

/**
 * Normalise any thrown ApiError into a message plus the structured `errors[]`
 * that `/discover` returns inside a 502 `detail` object.
 */
function describeError(err) {
  const detail = err?.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return {
      message: detail.message || err?.message || 'The request failed.',
      errors: Array.isArray(detail.errors) ? detail.errors : [],
    }
  }
  return { message: err?.message || 'The request failed.', errors: [] }
}

const errorLine = (item) =>
  typeof item === 'string'
    ? item
    : [item?.stage, item?.code].filter(Boolean).join(' / ') + `: ${item?.message ?? 'unknown error'}`

// ── Shared presentational bits ───────────────────────────────────────────────

const panelStyle = {
  background: 'var(--canvas-bg)',
  border: '1.5px solid var(--border-ink)',
  borderRadius: 'var(--radius-sm)',
  padding: '14px 16px',
  boxShadow: 'var(--shadow-solid-sm)',
}

const codeBlockStyle = {
  background: '#FFFFFF',
  border: '1px solid var(--border-ink)',
  borderRadius: 6,
  padding: '10px 12px',
  fontSize: 12,
  color: 'var(--brand-blue)',
  wordBreak: 'break-all',
  whiteSpace: 'pre-wrap',
  margin: 0,
}

function Banner({ tone, children, onDismiss, dismissLabel }) {
  const tones = {
    error: { background: 'var(--brand-coral-bg)', color: '#9A2C14', icon: AlertTriangle, role: 'alert' },
    success: { background: '#ECFDF5', color: '#065F46', icon: CheckCircle2, role: 'status' },
    warning: { background: 'var(--brand-yellow-bg)', color: '#92400E', icon: AlertTriangle, role: 'status' },
  }
  const config = tones[tone] || tones.success
  const Icon = config.icon
  return (
    <div
      role={config.role}
      style={{
        background: config.background,
        border: '1.5px solid var(--border-ink)',
        color: config.color,
        padding: '12px 16px',
        borderRadius: 8,
        marginBottom: 18,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        boxShadow: 'var(--shadow-solid-sm)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, fontWeight: 600 }}>
        <Icon size={18} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
        <div>{children}</div>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel || 'Dismiss message'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1 }}
        >
          <XCircle size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

function ErrorList({ items }) {
  if (!items || items.length === 0) return null
  return (
    <ul style={{ margin: '6px 0 0 0', paddingLeft: 18, fontSize: 12, fontWeight: 500 }}>
      {items.map((item, index) => (
        <li key={index} style={{ marginBottom: 2 }}>{errorLine(item)}</li>
      ))}
    </ul>
  )
}

function FieldError({ id, children }) {
  if (!children) return null
  return (
    <div id={id} role="alert" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--brand-coral)', marginTop: 4 }}>
      {children}
    </div>
  )
}

function Busy({ label }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240, gap: 12 }}
    >
      <Loader size={34} className="spin" aria-hidden="true" style={{ color: 'var(--brand-blue)' }} />
      <div className="text-muted" style={{ fontSize: 13 }}>{label}</div>
    </div>
  )
}

/** `caller_identity` is the whole point of a connection test: it proves which
 *  account the credentials belong to, which catches a wrong-account paste. */
function ConnectionResult({ result }) {
  if (!result) return null
  const failed = result.status === 'error'
  const account = result.caller_identity?.account
  return (
    <div style={{ ...panelStyle, borderLeft: `5px solid ${failed ? 'var(--brand-coral)' : '#047857'}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        {failed ? <AlertTriangle size={16} aria-hidden="true" color="var(--brand-coral)" />
          : <CheckCircle2 size={16} aria-hidden="true" color="#047857" />}
        <strong style={{ fontSize: 13, color: 'var(--ink-900)' }}>
          {failed ? 'Connection failed' : result.status === 'connected_with_warnings' ? 'Connected, with warnings' : 'Connected'}
        </strong>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Credentials belong to AWS account
        </div>
        <div
          className="font-mono"
          style={{ fontSize: 20, fontWeight: 800, color: account ? 'var(--ink-900)' : 'var(--brand-coral)', letterSpacing: 0.5 }}
        >
          {account || 'not reported'}
        </div>
        {result.caller_identity?.arn && (
          <div className="font-mono text-xs text-muted" style={{ wordBreak: 'break-all', marginTop: 2 }}>
            {result.caller_identity.arn}
          </div>
        )}
        {!account && (
          <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 2 }}>
            The identity could not be read, so the account could not be confirmed.
          </div>
        )}
      </div>

      {!failed && (
        <div className="font-mono" style={{ fontSize: 12, color: 'var(--ink-700)' }}>
          Visible: {result.instance_count ?? 0} EC2 · {result.rds_count ?? 0} RDS · {result.alarm_count ?? 0} alarm(s)
          {result.region ? ` · ${result.region}` : ''}
        </div>
      )}

      {result.message && (
        <p style={{ margin: '8px 0 0 0', fontSize: 12, color: 'var(--ink-700)' }}>{result.message}</p>
      )}
      <ErrorList items={result.errors} />
    </div>
  )
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ClientManagement() {
  const { user, can } = useAuth()
  const canManage = can('manage_clients')
  const canDelete = can('delete_clients')

  // `useId` wraps its value in punctuation that is legal in an id attribute but
  // not in a CSS selector, so it is stripped: these ids are used by `htmlFor`,
  // `aria-describedby` and the footer buttons' `form` attribute, and one of them
  // ending up in a selector should not be a latent breakage.
  const rawId = useId()
  const idPrefix = useMemo(() => `cm-${rawId.replace(/[^a-zA-Z0-9]/g, '')}`, [rawId])
  const fieldId = useCallback((name) => `${idPrefix}-${name}`, [idPrefix])

  const [clients, setClients] = useState([])
  const [agents, setAgents] = useState([])
  const [staleAfterSeconds, setStaleAfterSeconds] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Tracked separately from `error`: folding a failed /agents call into an empty
  // list made an outage indistinguishable from "no agents installed".
  const [agentsError, setAgentsError] = useState(null)
  const [notice, setNotice] = useState(null)

  const [activeTab, setActiveTab] = useState('clients')
  const tabRefs = useRef({})

  const [showOnboard, setShowOnboard] = useState(false)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [fieldErrors, setFieldErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [created, setCreated] = useState(null)

  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [testError, setTestError] = useState(null)

  const [confirmAction, setConfirmAction] = useState(null)
  const [busyClientId, setBusyClientId] = useState(null)
  const [discoverSummary, setDiscoverSummary] = useState(null)
  const [discoverError, setDiscoverError] = useState(null)

  const [scheduleTarget, setScheduleTarget] = useState(null)
  const [scheduleForm, setScheduleForm] = useState(null)
  const [scheduleSaving, setScheduleSaving] = useState(false)
  const [scheduleError, setScheduleError] = useState(null)
  const [scheduleFieldErrors, setScheduleFieldErrors] = useState({})

  const [installClientId, setInstallClientId] = useState('')
  const [installLoading, setInstallLoading] = useState(false)
  const [installError, setInstallError] = useState(null)
  const [install, setInstall] = useState(null)
  const [copied, setCopied] = useState(false)
  const [copiedPulse, setCopiedPulse] = useState(false)
  const [agentTab, setAgentTab] = useState('pulse')

  // ── Connection & Integration Settings ──────────────────────────────────────
  const [connectionTarget, setConnectionTarget] = useState(null)
  const [connectionTab, setConnectionTab] = useState('links') // 'links' | 'edit'
  const [connectionForm, setConnectionForm] = useState(null)
  const [connectionSaving, setConnectionSaving] = useState(false)
  const [connectionError, setConnectionError] = useState(null)
  const [connectionFieldErrors, setConnectionFieldErrors] = useState({})
  const [cfTemplateText, setCfTemplateText] = useState(null)
  const [cfTemplateLoading, setCfTemplateLoading] = useState(false)
  const [cfTemplateError, setCfTemplateError] = useState(null)
  const [showCfYamlPreview, setShowCfYamlPreview] = useState(false)
  const [targetPulseCmd, setTargetPulseCmd] = useState(null)
  const [targetPulseLoading, setTargetPulseLoading] = useState(false)
  const [targetPulseError, setTargetPulseError] = useState(null)
  const [testConnLoading, setTestConnLoading] = useState(false)
  const [testConnResult, setTestConnResult] = useState(null)
  const [testConnError, setTestConnError] = useState(null)
  const [showSecretKey, setShowSecretKey] = useState(false)
  const [copiedKey, setCopiedKey] = useState(null)

  // ── Observability & IP Whitelist State ──────────────────────────────────────
  const [whitelistSaving, setWhitelistSaving] = useState(false)
  const [whitelistSuccess, setWhitelistSuccess] = useState(null)
  const [whitelistError, setWhitelistError] = useState(null)
  const [detectingIp, setDetectingIp] = useState(false)
  const [nginxSnippetPreview, setNginxSnippetPreview] = useState(null)
  const [loadingNginxSnippet, setLoadingNginxSnippet] = useState(false)
  const [copiedDashboardUrl, setCopiedDashboardUrl] = useState(false)

  // ── Data ───────────────────────────────────────────────────────────────────

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)
    setAgentsError(null)

    const [clientResult, agentResult] = await Promise.allSettled([getClients(), getAgents()])

    if (clientResult.status === 'fulfilled') {
      setClients(clientResult.value?.clients || [])
    } else {
      setError(describeError(clientResult.reason).message)
    }

    if (agentResult.status === 'fulfilled') {
      setAgents(agentResult.value?.agents || [])
      setStaleAfterSeconds(agentResult.value?.stale_after_seconds ?? null)
    } else {
      setAgents([])
      setStaleAfterSeconds(null)
      setAgentsError(describeError(agentResult.reason).message)
    }

    setLoading(false)
  }, [])

  useEffect(() => { fetchAllData() }, [fetchAllData])

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 3000)
    return () => clearTimeout(timer)
  }, [copied])

  useEffect(() => {
    if (!copiedPulse) return
    const timer = setTimeout(() => setCopiedPulse(false), 3000)
    return () => clearTimeout(timer)
  }, [copiedPulse])

  const onlineAgents = useMemo(() => agents.filter((a) => a.status === 'online').length, [agents])
  const unreportedAgents = useMemo(
    () => agents.filter((a) => (a.status || 'unknown') === 'unknown').length,
    [agents],
  )

  // ── Tabs ───────────────────────────────────────────────────────────────────

  const handleTabKeyDown = (event) => {
    const index = TAB_ORDER.indexOf(activeTab)
    let next = null
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = TAB_ORDER[(index + 1) % TAB_ORDER.length]
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = TAB_ORDER[(index - 1 + TAB_ORDER.length) % TAB_ORDER.length]
    else if (event.key === 'Home') next = TAB_ORDER[0]
    else if (event.key === 'End') next = TAB_ORDER[TAB_ORDER.length - 1]
    if (!next) return
    event.preventDefault()
    setActiveTab(next)
    tabRefs.current[next]?.focus()
  }

  // ── Onboarding ─────────────────────────────────────────────────────────────

  const openOnboard = () => {
    // The signed-in user owns the account they onboard. The previous default was
    // a specific engineer's name, baked into the bundle and stored per client.
    setFormData({ ...EMPTY_FORM, assigned_employee: user?.name || '' })
    setFieldErrors({})
    setSubmitError(null)
    setCreated(null)
    setTestResult(null)
    setTestError(null)
    setShowOnboard(true)
  }

  const closeOnboard = () => {
    setShowOnboard(false)
    setSubmitError(null)
    setFieldErrors({})
    setTestResult(null)
    setTestError(null)
    setCreated(null)
  }

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev))
  }

  /** Mirror of the create endpoint's validation, field by field. */
  const validateForm = () => {
    const errors = {}
    if (!formData.client_name.trim()) {
      errors.client_name = 'A client name is required.'
    }
    if (!ACCOUNT_ID_RE.test(formData.account_id.trim())) {
      errors.account_id = 'Must be a 12-digit AWS account ID.'
    }
    if (!REGION_RE.test(formData.aws_region.trim())) {
      errors.aws_region = 'Must look like a real region, for example ap-south-1.'
    }
    if (formData.console_url.trim() && !isValidConsoleUrl(formData.console_url)) {
      errors.console_url =
        'Must be an HTTPS URL on a recognised cloud console domain, with no port or embedded credentials.'
    }
    if (formData.access_type === 'api' && !(formData.iam_access_key.trim() && formData.iam_secret_key.trim())) {
      errors.iam_access_key = 'IAM API key access needs both the access key ID and the secret key.'
    }
    if (!HHMM_RE.test(formData.report_time.trim())) {
      errors.report_time = 'Must be a 24-hour time in HH:MM format.'
    }
    const rejected = invalidRecipients(formData.recipient_emails)
    if (rejected.length > 0) {
      errors.recipient_emails = `These addresses are not valid: ${rejected.join(', ')}`
    } else if (formData.report_enabled && validRecipients(formData.recipient_emails).length === 0) {
      errors.recipient_emails = 'Scheduled delivery needs at least one valid recipient address.'
    }
    return errors
  }

  const handleSubmitClient = async (event) => {
    event.preventDefault()
    setSubmitError(null)

    const errors = validateForm()
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setSubmitting(true)
    try {
      const response = await createClient({
        client_name: formData.client_name.trim(),
        account_id: formData.account_id.trim(),
        aws_region: formData.aws_region.trim(),
        cloud_provider: formData.cloud_provider,
        access_type: formData.access_type,
        console_url: formData.console_url.trim(),
        iam_username: formData.iam_username.trim(),
        iam_access_key: formData.iam_access_key,
        iam_secret_key: formData.iam_secret_key,
        report_time: formData.report_time.trim(),
        report_enabled: formData.report_enabled,
        recipient_emails: formData.recipient_emails.trim(),
        assigned_employee: formData.assigned_employee.trim() || user?.name || '',
      })
      // The create call no longer discovers resources, so the dialog switches to
      // a hand-off panel rather than closing on a half-finished setup.
      setCreated({ ...response, client_name: formData.client_name.trim(), discovery: null, discoveryError: null })
      setTestResult(null)
      setTestError(null)
      await fetchAllData()
    } catch (err) {
      setSubmitError(describeError(err).message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleTestConnection = async () => {
    setTesting(true)
    setTestError(null)
    setTestResult(null)
    const region = formData.aws_region.trim()
    const accountId = formData.account_id.trim()

    if (!REGION_RE.test(region)) {
      setTestError('Enter a valid region before testing the connection.')
      setTesting(false)
      return
    }

    // The endpoint needs either a role ARN or a key pair. For the keyless mode
    // the ARN is derived the same way the server derives it at creation time.
    const roleArn =
      formData.access_type === 'iam_role' && ACCOUNT_ID_RE.test(accountId)
        ? `arn:aws:iam::${accountId}:role/CloudIQStudioReadRole`
        : ''
    const accessKey = formData.access_type === 'api' ? formData.iam_access_key.trim() : ''
    const secretKey = formData.access_type === 'api' ? formData.iam_secret_key : ''

    if (!roleArn && !(accessKey && secretKey)) {
      setTestError(
        'Provide either a 12-digit account ID for keyless role access, or both halves of an IAM key pair.',
      )
      setTesting(false)
      return
    }
    if (roleArn && !ROLE_ARN_RE.test(roleArn)) {
      setTestError('The derived role ARN is not valid. Check the account ID.')
      setTesting(false)
      return
    }

    try {
      const result = await testConnection({
        role_arn: roleArn,
        external_id: '',
        aws_region: region,
        access_key: accessKey,
        secret_key: secretKey,
        account_id: accountId,
      })
      setTestResult(result)
    } catch (err) {
      setTestError(describeError(err).message)
    } finally {
      setTesting(false)
    }
  }

  const handleDiscoverForCreated = async () => {
    if (!created?.client_id) return
    setBusyClientId(created.client_id)
    try {
      const result = await discoverResources(created.client_id)
      setCreated((prev) => (prev ? { ...prev, discovery: result, discoveryError: null } : prev))
      await fetchAllData()
    } catch (err) {
      setCreated((prev) => (prev ? { ...prev, discovery: null, discoveryError: describeError(err) } : prev))
    } finally {
      setBusyClientId(null)
    }
  }

  // ── Discovery and deletion from the client cards ───────────────────────────

  const executeConfirmedAction = async () => {
    if (!confirmAction) return
    const { type, clientId, clientName } = confirmAction
    setConfirmAction(null)
    setDiscoverSummary(null)
    setDiscoverError(null)

    if (type === 'discover') {
      setBusyClientId(clientId)
      try {
        const result = await discoverResources(clientId)
        setDiscoverSummary({ clientName, ...result })
        await fetchAllData()
      } catch (err) {
        setDiscoverError({ clientName, ...describeError(err) })
      } finally {
        setBusyClientId(null)
      }
      return
    }

    if (type === 'delete') {
      setBusyClientId(clientId)
      try {
        await deleteClient(clientId)
        setNotice(`Client "${clientName}" and its telemetry history were deleted.`)
        await fetchAllData()
      } catch (err) {
        setError(`Could not delete "${clientName}": ${describeError(err).message}`)
      } finally {
        setBusyClientId(null)
      }
    }
  }

  // ── Schedule editing ───────────────────────────────────────────────────────

  const openSchedule = (client) => {
    setScheduleTarget(client)
    setScheduleForm({
      report_time: client.report_time || '11:00',
      report_enabled: client.report_enabled === true,
      recipient_emails: client.recipient_emails || '',
      assigned_employee: client.assigned_employee || '',
      aws_region: client.aws_region || '',
    })
    setScheduleError(null)
    setScheduleFieldErrors({})
  }

  const closeSchedule = () => {
    setScheduleTarget(null)
    setScheduleForm(null)
    setScheduleError(null)
    setScheduleFieldErrors({})
  }

  const handleScheduleChange = (event) => {
    const { name, value, type, checked } = event.target
    setScheduleForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
    setScheduleFieldErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev))
  }

  const handleSaveSchedule = async (event) => {
    event.preventDefault()
    if (!scheduleTarget || !scheduleForm) return
    setScheduleError(null)

    const errors = {}
    if (!HHMM_RE.test((scheduleForm.report_time || '').trim())) {
      errors.report_time = 'Must be a 24-hour time in HH:MM format.'
    }
    if (!REGION_RE.test((scheduleForm.aws_region || '').trim())) {
      errors.aws_region = 'Must look like a real region, for example ap-south-1.'
    }
    const rejected = invalidRecipients(scheduleForm.recipient_emails)
    if (rejected.length > 0) {
      errors.recipient_emails = `These addresses are not valid: ${rejected.join(', ')}`
    } else if (scheduleForm.report_enabled && validRecipients(scheduleForm.recipient_emails).length === 0) {
      errors.recipient_emails = 'Add at least one valid recipient before enabling scheduled delivery.'
    }
    setScheduleFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    // PATCH rejects an empty body with a 400, so only genuine changes are sent.
    const payload = {}
    if (scheduleForm.report_time.trim() !== (scheduleTarget.report_time || '')) {
      payload.report_time = scheduleForm.report_time.trim()
    }
    if (scheduleForm.report_enabled !== (scheduleTarget.report_enabled === true)) {
      payload.report_enabled = scheduleForm.report_enabled
    }
    if (scheduleForm.recipient_emails.trim() !== (scheduleTarget.recipient_emails || '').trim()) {
      payload.recipient_emails = scheduleForm.recipient_emails.trim()
    }
    if (scheduleForm.assigned_employee.trim() !== (scheduleTarget.assigned_employee || '')) {
      payload.assigned_employee = scheduleForm.assigned_employee.trim()
    }
    if (scheduleForm.aws_region.trim() !== (scheduleTarget.aws_region || '')) {
      payload.aws_region = scheduleForm.aws_region.trim()
    }

    if (Object.keys(payload).length === 0) {
      setScheduleError('Nothing has changed, so there is nothing to save.')
      return
    }

    setScheduleSaving(true)
    try {
      const result = await updateClient(scheduleTarget.id, payload)
      const changed = Array.isArray(result?.updated_fields) ? result.updated_fields.join(', ') : Object.keys(payload).join(', ')
      setNotice(`Updated ${scheduleTarget.client_name}: ${changed}.`)
      closeSchedule()
      await fetchAllData()
    } catch (err) {
      setScheduleError(describeError(err).message)
    } finally {
      setScheduleSaving(false)
    }
  }

  // ── Install command ────────────────────────────────────────────────────────

  const handleFetchInstall = async () => {
    const clientId = Number(installClientId)
    if (!clientId) {
      setInstallError('Choose a client first. The command embeds that client’s own agent token.')
      setInstall(null)
      return
    }
    setInstallLoading(true)
    setInstallError(null)
    setInstall(null)
    try {
      setInstall(await getAgentInstallCommand(clientId))
    } catch (err) {
      // A 503 means PUBLIC_BASE_URL is unset. The server's explanation is shown
      // as-is; guessing a URL here would produce a command that cannot work.
      setInstallError(describeError(err).message)
    } finally {
      setInstallLoading(false)
    }
  }

  const handleCopyCommand = async () => {
    if (!install?.command) return
    try {
      await navigator.clipboard.writeText(install.command)
      setCopied(true)
    } catch {
      setInstallError('The browser blocked clipboard access. Select the command and copy it manually.')
    }
  }

  const handleCopyPulseCommand = async () => {
    const pulseCmd = install?.pulse_command || install?.command
    if (!pulseCmd) return
    try {
      await navigator.clipboard.writeText(pulseCmd)
      setCopiedPulse(true)
    } catch {
      setInstallError('The browser blocked clipboard access. Select the command and copy it manually.')
    }
  }

  // ── Connection & Integration Settings ──────────────────────────────────────

  const copyToClipboard = async (text, keyName) => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(keyName)
      setTimeout(() => setCopiedKey((curr) => (curr === keyName ? null : curr)), 2500)
    } catch {
      /* ignore */
    }
  }

  const handleDownloadCfYaml = (client, text) => {
    const yamlContent = text || cfTemplateText
    if (!yamlContent) return
    const blob = new Blob([yamlContent], { type: 'text/yaml' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cloudiq-role-${client.account_id}.yaml`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const openConnectionModal = async (client, defaultTab = 'links') => {
    setConnectionTarget(client)
    setConnectionTab(defaultTab)
    setConnectionForm({
      client_name: client.client_name || '',
      access_type: client.access_type || 'iam_role',
      role_arn: client.role_arn || (client.account_id ? `arn:aws:iam::${client.account_id}:role/CloudIQStudioReadRole` : ''),
      external_id: client.external_id || '',
      aws_region: client.aws_region || '',
      console_url: client.console_url || '',
      iam_username: client.iam_username || '',
      iam_access_key: '',
      iam_secret_key: '',
      allowed_dashboard_ips: client.allowed_dashboard_ips || '',
    })
    setConnectionError(null)
    setConnectionFieldErrors({})
    setCfTemplateText(null)
    setCfTemplateError(null)
    setShowCfYamlPreview(false)
    setTargetPulseCmd(null)
    setTargetPulseError(null)
    setTestConnResult(null)
    setTestConnError(null)
    setShowSecretKey(false)
    setCopiedKey(null)
    setWhitelistSuccess(null)
    setWhitelistError(null)
    setNginxSnippetPreview(null)

    setCfTemplateLoading(true)
    setTargetPulseLoading(true)

    try {
      const tmpl = await getCloudFormationTemplate(client.id)
      setCfTemplateText(typeof tmpl === 'string' ? tmpl : JSON.stringify(tmpl, null, 2))
    } catch (e) {
      setCfTemplateError(describeError(e).message)
    } finally {
      setCfTemplateLoading(false)
    }

    try {
      const cmdData = await getAgentInstallCommand(client.id)
      setTargetPulseCmd(cmdData?.pulse_command || cmdData?.command || '')
    } catch (e) {
      setTargetPulseError(describeError(e).message)
    } finally {
      setTargetPulseLoading(false)
    }
  }

  const closeConnectionModal = () => {
    setConnectionTarget(null)
    setConnectionForm(null)
    setConnectionError(null)
    setTestConnResult(null)
    setTestConnError(null)
    setWhitelistSuccess(null)
    setWhitelistError(null)
    setNginxSnippetPreview(null)
  }

  const handleAddMyIp = async () => {
    setDetectingIp(true)
    setWhitelistError(null)
    try {
      const data = await getMyIp()
      if (data?.ip) {
        setConnectionForm((prev) => {
          const current = (prev?.allowed_dashboard_ips || '').trim()
          const ips = current ? current.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean) : []
          if (!ips.includes(data.ip)) {
            ips.push(data.ip)
          }
          return { ...prev, allowed_dashboard_ips: ips.join(', ') }
        })
      }
    } catch (e) {
      setWhitelistError('Could not auto-detect public IP. Please enter it manually.')
    } finally {
      setDetectingIp(false)
    }
  }

  const handleSaveWhitelistOnly = async () => {
    if (!connectionTarget || !connectionForm) return
    setWhitelistSaving(true)
    setWhitelistError(null)
    setWhitelistSuccess(null)
    try {
      const result = await updateClientWhitelist(connectionTarget.id, {
        allowed_dashboard_ips: connectionForm.allowed_dashboard_ips || '',
      })
      setWhitelistSuccess(`Successfully updated dashboard IP whitelist for ${connectionTarget.client_name}.`)
      await fetchAllData()
    } catch (err) {
      setWhitelistError(describeError(err).message)
    } finally {
      setWhitelistSaving(false)
    }
  }

  const handleLoadNginxSnippet = async () => {
    if (nginxSnippetPreview) {
      setNginxSnippetPreview(null)
      return
    }
    setLoadingNginxSnippet(true)
    try {
      const snippet = await getNginxWhitelistSnippet()
      setNginxSnippetPreview(typeof snippet === 'string' ? snippet : JSON.stringify(snippet, null, 2))
    } catch (err) {
      setNginxSnippetPreview(null)
    } finally {
      setLoadingNginxSnippet(false)
    }
  }

  const handleConnectionFormChange = (event) => {
    const { name, value } = event.target
    setConnectionForm((prev) => ({ ...prev, [name]: value }))
    setConnectionFieldErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev))
  }

  const handleTestConnectionInModal = async () => {
    if (!connectionTarget || !connectionForm) return
    setTestConnLoading(true)
    setTestConnError(null)
    setTestConnResult(null)

    const region = (connectionForm.aws_region || connectionTarget.aws_region || '').trim()
    const accountId = connectionTarget.account_id

    if (region && !REGION_RE.test(region)) {
      setTestConnError('Enter a valid AWS region (e.g. ap-south-1) before testing.')
      setTestConnLoading(false)
      return
    }

    const roleArn =
      connectionForm.access_type === 'iam_role'
        ? (connectionForm.role_arn || `arn:aws:iam::${accountId}:role/CloudIQStudioReadRole`).trim()
        : ''
    const externalId = (connectionForm.external_id || connectionTarget.external_id || '').trim()
    const accessKey = connectionForm.access_type === 'api' ? connectionForm.iam_access_key.trim() : ''
    const secretKey = connectionForm.access_type === 'api' ? connectionForm.iam_secret_key : ''

    if (connectionForm.access_type === 'iam_role' && roleArn && !ROLE_ARN_RE.test(roleArn)) {
      setTestConnError('The role ARN is not formatted correctly.')
      setTestConnLoading(false)
      return
    }

    try {
      const result = await testConnection({
        role_arn: roleArn,
        external_id: externalId,
        aws_region: region,
        access_key: accessKey,
        secret_key: secretKey,
        account_id: accountId,
      })
      setTestConnResult(result)
    } catch (err) {
      setTestConnError(describeError(err).message)
    } finally {
      setTestConnLoading(false)
    }
  }

  const handleSaveConnection = async (event) => {
    event.preventDefault()
    if (!connectionTarget || !connectionForm) return
    setConnectionError(null)

    const errors = {}
    if (!connectionForm.client_name.trim()) {
      errors.client_name = 'Client name cannot be blank.'
    }
    if (connectionForm.aws_region.trim() && !REGION_RE.test(connectionForm.aws_region.trim())) {
      errors.aws_region = 'Must look like a real region, for example ap-south-1.'
    }
    if (connectionForm.access_type === 'iam_role') {
      const arn = connectionForm.role_arn.trim()
      if (arn && !ROLE_ARN_RE.test(arn)) {
        errors.role_arn = 'Must be a valid IAM role ARN.'
      }
    }
    if (connectionForm.access_type === 'api') {
      const hasExistingKeys = connectionTarget.has_iam_access_key && connectionTarget.has_iam_secret_key
      if (!hasExistingKeys && !(connectionForm.iam_access_key.trim() && connectionForm.iam_secret_key.trim())) {
        errors.iam_access_key = 'Both access key ID and secret access key are required for API mode.'
      }
    }
    if (connectionForm.console_url.trim() && !isValidConsoleUrl(connectionForm.console_url)) {
      errors.console_url = 'Must be an HTTPS URL on a recognised cloud console domain.'
    }

    setConnectionFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    const payload = {}
    if (connectionForm.client_name.trim() !== (connectionTarget.client_name || '')) {
      payload.client_name = connectionForm.client_name.trim()
    }
    if (connectionForm.access_type !== (connectionTarget.access_type || '')) {
      payload.access_type = connectionForm.access_type
    }
    if (connectionForm.role_arn.trim() !== (connectionTarget.role_arn || '')) {
      payload.role_arn = connectionForm.role_arn.trim()
    }
    if (connectionForm.external_id.trim() !== (connectionTarget.external_id || '')) {
      payload.external_id = connectionForm.external_id.trim()
    }
    if (connectionForm.aws_region.trim() !== (connectionTarget.aws_region || '')) {
      payload.aws_region = connectionForm.aws_region.trim()
    }
    if (connectionForm.console_url.trim() !== (connectionTarget.console_url || '')) {
      payload.console_url = connectionForm.console_url.trim()
    }
    if (connectionForm.iam_username.trim() !== (connectionTarget.iam_username || '')) {
      payload.iam_username = connectionForm.iam_username.trim()
    }
    if (connectionForm.iam_access_key.trim()) {
      payload.iam_access_key = connectionForm.iam_access_key.trim()
    }
    if (connectionForm.iam_secret_key.trim()) {
      payload.iam_secret_key = connectionForm.iam_secret_key.trim()
    }
    if (connectionForm.allowed_dashboard_ips !== undefined && connectionForm.allowed_dashboard_ips.trim() !== (connectionTarget.allowed_dashboard_ips || '')) {
      payload.allowed_dashboard_ips = connectionForm.allowed_dashboard_ips.trim()
    }

    if (Object.keys(payload).length === 0) {
      setConnectionError('Nothing has changed, so there is nothing to save.')
      return
    }

    setConnectionSaving(true)
    try {
      const result = await updateClient(connectionTarget.id, payload)
      const changed = Array.isArray(result?.updated_fields) ? result.updated_fields.join(', ') : Object.keys(payload).join(', ')
      setNotice(`Updated ${connectionTarget.client_name} connection: ${changed}.`)
      closeConnectionModal()
      await fetchAllData()
    } catch (err) {
      setConnectionError(describeError(err).message)
    } finally {
      setConnectionSaving(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const thresholdLabel = formatThreshold(staleAfterSeconds)

  return (
    <div className="animate-in">
      <div className="flex-between mb-3">
        <div>
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 800, color: 'var(--ink-900)' }}>
            Client Accounts &amp; Telemetry Agents
          </h2>
          <p style={{ fontSize: 13, color: 'var(--ink-600)', marginTop: 2 }}>
            Cross-account access, scheduled report delivery, and collector heartbeat health.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" className="btn btn-secondary" onClick={fetchAllData} disabled={loading}>
            <RefreshCw size={15} aria-hidden="true" className={loading ? 'spin' : ''} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          {canManage && (
            <button type="button" className="btn btn-primary" onClick={openOnboard}>
              <Plus size={16} aria-hidden="true" /> Onboard client
            </button>
          )}
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Client management views"
        onKeyDown={handleTabKeyDown}
        style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1.5px solid var(--border-ink)', paddingBottom: 10 }}
      >
        <button
          type="button"
          role="tab"
          id="cm-tab-clients"
          aria-selected={activeTab === 'clients'}
          aria-controls="cm-panel-clients"
          tabIndex={activeTab === 'clients' ? 0 : -1}
          ref={(node) => { tabRefs.current.clients = node }}
          onClick={() => setActiveTab('clients')}
          className={`btn ${activeTab === 'clients' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ padding: '8px 18px', fontSize: 13 }}
        >
          <Users size={15} aria-hidden="true" /> Client accounts ({clients.length})
        </button>
        <button
          type="button"
          role="tab"
          id="cm-tab-agents"
          aria-selected={activeTab === 'agents'}
          aria-controls="cm-panel-agents"
          tabIndex={activeTab === 'agents' ? 0 : -1}
          ref={(node) => { tabRefs.current.agents = node }}
          onClick={() => setActiveTab('agents')}
          className={`btn ${activeTab === 'agents' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ padding: '8px 18px', fontSize: 13 }}
        >
          <Server size={15} aria-hidden="true" /> Collector agents ({agents.length})
          {agentsError ? (
            <span className="badge badge-alarm" style={{ marginLeft: 6, fontSize: 10 }}>Unavailable</span>
          ) : (
            <span className="badge badge-ok" style={{ marginLeft: 6, fontSize: 10 }}>{onlineAgents} online</span>
          )}
        </button>
      </div>

      {notice && (
        <Banner tone="success" onDismiss={() => setNotice(null)} dismissLabel="Dismiss confirmation">
          {notice}
        </Banner>
      )}

      {error && (
        <Banner tone="error" onDismiss={() => setError(null)} dismissLabel="Dismiss error">
          {error}
        </Banner>
      )}

      {discoverSummary && (
        <Banner tone={discoverSummary.errors?.length ? 'warning' : 'success'} onDismiss={() => setDiscoverSummary(null)} dismissLabel="Dismiss discovery result">
          <div>
            Discovery for <strong>{discoverSummary.clientName}</strong> finished with status{' '}
            <strong>{discoverSummary.status}</strong>: {discoverSummary.discovered ?? 0} resource(s) stored —{' '}
            {discoverSummary.ec2_count ?? 0} EC2, {discoverSummary.rds_count ?? 0} RDS, {discoverSummary.alarm_count ?? 0} alarm(s).
            <ErrorList items={discoverSummary.errors} />
          </div>
        </Banner>
      )}

      {discoverError && (
        <Banner tone="error" onDismiss={() => setDiscoverError(null)} dismissLabel="Dismiss discovery error">
          <div>
            Discovery for <strong>{discoverError.clientName}</strong> failed: {discoverError.message}
            <ErrorList items={discoverError.errors} />
          </div>
        </Banner>
      )}

      {/* ── Clients ── */}
      <div
        id="cm-panel-clients"
        role="tabpanel"
        aria-labelledby="cm-tab-clients"
        hidden={activeTab !== 'clients'}
        tabIndex={-1}
      >
        {activeTab === 'clients' && (
          loading && clients.length === 0 ? (
            <Busy label="Loading client accounts…" />
          ) : clients.length === 0 && !error ? (
            <div className="card empty-state" style={{ padding: '60px 20px' }}>
              <Users size={36} aria-hidden="true" style={{ color: 'var(--brand-blue)', margin: '0 auto 12px' }} />
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 18, color: 'var(--ink-900)', marginBottom: 6 }}>
                No clients onboarded yet
              </h3>
              <p style={{ fontSize: 13, color: 'var(--ink-500)', maxWidth: 440, margin: '0 auto 20px' }}>
                Connect a client account with a keyless cross-account role, an IAM key pair or the Linux collector
                agent to start scheduling daily reports.
              </p>
              {canManage && (
                <button type="button" className="btn btn-primary" onClick={openOnboard}>
                  <Plus size={16} aria-hidden="true" /> Onboard the first client
                </button>
              )}
            </div>
          ) : (
            <div className="grid-2">
              {clients.map((client) => {
                const busy = busyClientId === client.id
                return (
                  <div className="card" key={client.id} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="card-header">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="badge badge-info">{client.cloud_provider || 'AWS'}</span>
                          <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-900)', margin: 0 }}>
                            {client.client_name}
                          </h3>
                        </div>
                        <div className="font-mono" style={{ fontSize: 11.5, color: 'var(--ink-500)', marginTop: 4 }}>
                          Account <strong>{client.account_id}</strong>
                          {client.aws_region ? <> · Region <strong>{client.aws_region}</strong></> : <> · <em>region not set</em></>}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          className="btn-icon"
                          aria-label={`Connection settings and integration links for ${client.client_name}`}
                          title="Connection & Integration Setup"
                          onClick={() => openConnectionModal(client)}
                        >
                          <Key size={15} aria-hidden="true" style={{ color: '#D97706' }} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          aria-label={`Observability dashboard whitelist for ${client.client_name}`}
                          title="Client Observability & IP Whitelist"
                          onClick={() => openConnectionModal(client, 'whitelist')}
                        >
                          <Shield size={15} aria-hidden="true" style={{ color: client.allowed_dashboard_ips ? '#10B981' : '#64748B' }} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          aria-label={`Run resource discovery for ${client.client_name}`}
                          title="Run resource discovery"
                          onClick={() => setConfirmAction({ type: 'discover', clientId: client.id, clientName: client.client_name })}
                          disabled={busy}
                        >
                          <RefreshCw size={14} aria-hidden="true" className={busy ? 'spin' : ''} />
                        </button>
                        {canManage && (
                          <button
                            type="button"
                            className="btn-icon"
                            aria-label={`Edit report schedule for ${client.client_name}`}
                            title="Edit report schedule"
                            onClick={() => openSchedule(client)}
                          >
                            <Clock size={15} aria-hidden="true" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="btn-icon"
                            aria-label={`Delete ${client.client_name}`}
                            title="Delete client"
                            onClick={() => setConfirmAction({ type: 'delete', clientId: client.id, clientName: client.client_name })}
                            disabled={busy}
                            style={{ color: 'var(--brand-coral)' }}
                          >
                            <Trash2 size={15} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="card-body" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 10,
                          background: 'var(--canvas-bg)',
                          padding: '10px 14px',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border-ink)',
                        }}
                      >
                        <div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                            Access mode
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                            <Key size={14} aria-hidden="true" color="var(--brand-yellow)" />
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-900)' }}>
                              {ACCESS_MODE_LABELS[client.access_type] || 'Not recorded'}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => openConnectionModal(client)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              marginTop: 4,
                              fontSize: 11,
                              color: 'var(--brand-blue, #2563EB)',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              textDecoration: 'underline',
                              fontWeight: 600,
                            }}
                          >
                            Integration links &amp; setup →
                          </button>
                        </div>
                        <div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                            Stored credentials
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                            <Shield size={14} aria-hidden="true" color="var(--brand-teal)" />
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink-900)' }}>
                              {client.has_iam_access_key && client.has_iam_secret_key
                                ? 'Key pair held in vault'
                                : client.has_iam_access_key || client.has_iam_secret_key
                                  ? 'Incomplete key pair'
                                  : 'None — keyless'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink-700)', textTransform: 'uppercase', marginBottom: 6 }}>
                          Discovered resources ({client.resources?.length || 0})
                        </div>
                        {client.resources?.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 100, overflowY: 'auto' }}>
                            {client.resources.map((res) => (
                              <span key={res.resource_id || res.name} className="badge badge-stable" style={{ fontSize: 11 }}>
                                <Server size={11} aria-hidden="true" /> {res.name}
                                {res.type ? <span className="text-muted">&nbsp;· {res.type}</span> : null}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: 'var(--ink-500)', fontStyle: 'italic' }}>
                            Nothing discovered yet. Run discovery to enumerate EC2 and RDS.
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11.5, color: 'var(--ink-600)' }}>
                        <div>
                          <Mail size={12} aria-hidden="true" style={{ verticalAlign: -2 }} /> Delivery{' '}
                          {client.report_enabled ? (
                            <span className="badge badge-ok" style={{ fontSize: 10 }}>On</span>
                          ) : (
                            <span className="badge badge-stable" style={{ fontSize: 10 }}>Off</span>
                          )}
                        </div>
                        <div>
                          <Clock size={12} aria-hidden="true" style={{ verticalAlign: -2 }} />{' '}
                          {client.report_time ? <>Scheduled <strong>{client.report_time}</strong></> : <em>No time set</em>}
                        </div>
                        <div>
                          {client.recipient_count > 0
                            ? <>{client.recipient_count} recipient{client.recipient_count === 1 ? '' : 's'}</>
                            : <em>No recipients</em>}
                        </div>
                        <div>
                          {client.last_scheduled_run
                            ? <>Last run <strong>{client.last_scheduled_run}</strong></>
                            : <em>Never run</em>}
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <Globe size={12} aria-hidden="true" style={{ verticalAlign: -2 }} /> Observability Portal:{' '}
                          {client.allowed_dashboard_ips ? (
                            <span style={{ color: '#047857', fontWeight: 600 }}>Protected (IP Whitelisted)</span>
                          ) : (
                            <span style={{ color: 'var(--ink-400)' }}>Unconfigured (Restricted)</span>
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: 'auto',
                          paddingTop: 10,
                          borderTop: '1px solid var(--border-subtle)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 10,
                          fontSize: 11.5,
                          color: 'var(--ink-500)',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span>
                          Assigned: {client.assigned_employee ? <strong>{client.assigned_employee}</strong> : <em>unassigned</em>}
                        </span>
                        <span>
                          Onboarded by: {client.created_by ? <strong>{client.created_by}</strong> : <em>not recorded</em>}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        )}
      </div>

      {/* ── Agents ── */}
      <div
        id="cm-panel-agents"
        role="tabpanel"
        aria-labelledby="cm-tab-agents"
        hidden={activeTab !== 'agents'}
        tabIndex={-1}
      >
        {activeTab === 'agents' && (
          <div>
            {canManage && (
              <div style={{ ...panelStyle, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Terminal size={18} aria-hidden="true" color="var(--brand-blue)" />
                  <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 15, fontWeight: 800, margin: 0, color: 'var(--ink-900)' }}>
                    Collector agent install command
                  </h3>
                </div>
                <p style={{ margin: '0 0 12px 0', fontSize: 12.5, color: 'var(--ink-600)' }}>
                  The command is issued per client and carries that client&apos;s own agent token, so heartbeats are
                  attributed correctly. It is generated by the server from its configured public URL.
                </p>

                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                  <div style={{ minWidth: 260 }}>
                    <label className="form-label" htmlFor={fieldId('install-client')}>Client</label>
                    <select
                      id={fieldId('install-client')}
                      className="form-select"
                      value={installClientId}
                      onChange={(event) => {
                        setInstallClientId(event.target.value)
                        setInstall(null)
                        setInstallError(null)
                      }}
                    >
                      <option value="">Select a client…</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.client_name} ({client.account_id})
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleFetchInstall}
                    disabled={installLoading || clients.length === 0}
                  >
                    {installLoading
                      ? <Loader size={15} aria-hidden="true" className="spin" />
                      : <Terminal size={15} aria-hidden="true" />}
                    {installLoading ? 'Fetching…' : 'Get install command'}
                  </button>
                </div>

                {installError && (
                  <div role="alert" style={{ fontSize: 12.5, fontWeight: 600, color: '#9A2C14', marginBottom: 10 }}>
                    {installError}
                  </div>
                )}

                {install && (
                  <div style={{ marginTop: 12 }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink-900)' }}>
                            CloudIQ Pulse Agent (<code>cloudpulse-agent</code>) — Enterprise Telemetry &amp; Security Daemon
                          </div>
                          <div className="text-xs text-muted" style={{ marginTop: 2 }}>
                            Streams real-time 60s CPU/RAM/Disk metrics, OS inventories, and security/application events to <strong>Pulse Telemetry</strong>.
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleCopyPulseCommand}
                            aria-label={`Copy CloudIQ Pulse command for ${install.client_name}`}
                            style={{ padding: '5px 14px', fontSize: 12 }}
                          >
                            {copiedPulse ? <Check size={14} aria-hidden="true" color="#fff" /> : <Copy size={14} aria-hidden="true" />}
                            {copiedPulse ? 'Copied Pulse Command' : 'Copy Pulse Command'}
                          </button>
                        </div>
                      </div>
                      <pre className="font-mono" style={{ ...codeBlockStyle, borderLeft: '4px solid var(--brand-coral)' }}>
                        {install.pulse_command || install.command}
                      </pre>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, fontSize: 12 }}>
                        <span className="text-muted">✓ Single command installs <code>cloudpulse-agent.service</code>, <code>cloudpulse</code> CLI, and 60s telemetry probe.</span>
                        <a href="/pulse" style={{ color: 'var(--brand-blue)', fontWeight: 600, textDecoration: 'none' }}>
                          Open Pulse Telemetry →
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="card">
              <div className="card-header">
                <div>
                  <h3>Registered collector agents ({agents.length})</h3>
                  <div className="text-xs text-muted" style={{ marginTop: 2 }}>
                    {thresholdLabel
                      ? `A heartbeat older than ${thresholdLabel} is reported as stale.`
                      : 'Staleness threshold not reported by the server.'}
                    {unreportedAgents > 0 && ` ${unreportedAgents} agent(s) have never reported.`}
                  </div>
                </div>
              </div>

              <div style={{ padding: 0, overflowX: 'auto' }}>
                {agentsError ? (
                  <div style={{ padding: '24px 20px' }}>
                    <Banner tone="error">
                      <div>
                        The agent fleet could not be loaded, so this list is not a picture of reality: {agentsError}
                        <div style={{ marginTop: 10 }}>
                          <button type="button" className="btn btn-secondary" onClick={fetchAllData} style={{ padding: '5px 12px', fontSize: 12 }}>
                            <RefreshCw size={13} aria-hidden="true" /> Try again
                          </button>
                        </div>
                      </div>
                    </Banner>
                  </div>
                ) : loading && agents.length === 0 ? (
                  <Busy label="Loading collector agents…" />
                ) : agents.length === 0 ? (
                  <div className="empty-state" style={{ padding: '40px 20px' }}>
                    <Server size={36} aria-hidden="true" style={{ color: 'var(--brand-blue)', margin: '0 auto 10px' }} />
                    <h4 style={{ fontFamily: 'var(--font-serif)', color: 'var(--ink-900)' }}>No agents have reported</h4>
                    <p style={{ fontSize: 12.5, maxWidth: 460, margin: '0 auto' }}>
                      Install the collector on a client host using the per-client command above. Heartbeats appear here
                      once the agent runs; there is no way to inject a test heartbeat from the console.
                    </p>
                  </div>
                ) : (
                  <table className="data-table">
                    <caption className="sr-only">
                      Collector agents with heartbeat status, host details, linked client and latest resource usage.
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">Status</th>
                        <th scope="col">Host</th>
                        <th scope="col">Linked client</th>
                        <th scope="col">Agent token</th>
                        <th scope="col">CPU</th>
                        <th scope="col">Memory</th>
                        <th scope="col">Disk</th>
                        <th scope="col">Last heartbeat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...agents].sort((a, b) => {
                        const kA = (a.client_name || a.hostname || '').toLowerCase()
                        const kB = (b.client_name || b.hostname || '').toLowerCase()
                        return kA.localeCompare(kB)
                      }).map((ag) => {
                        const state = agentState(ag.status)
                        const age = formatRelativeAge(ag.heartbeat_age_seconds)
                        const hostTitle = ag.server_name || (ag.hostname && ag.hostname !== ag.ip_address 
                          ? ag.hostname 
                          : (ag.client_name ? `${ag.client_name} Node` : 'Unnamed Host'))
                        return (
                          <tr key={ag.id}>
                            <td>
                              <span className={`badge ${state.badge}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span
                                  aria-hidden="true"
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: state.dot,
                                    border: state.hollow ? '2px solid currentColor' : 'none',
                                  }}
                                />
                                {state.label}
                              </span>
                            </td>
                            <td>
                              <div style={{ fontWeight: 800, color: 'var(--ink-900)' }}>{hostTitle}</div>
                              <div className="font-mono text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                <span>{ag.ip_address || <em>IP not reported</em>}</span>
                                {ag.instance_id && (
                                  <span style={{ color: 'var(--ink-500)' }}>({ag.instance_id})</span>
                                )}
                                {ag.hostname && ag.hostname.startsWith('ip-') && (
                                  <span className="badge badge-stable" style={{ fontSize: 9, padding: '1px 5px' }}>EC2</span>
                                )}
                              </div>
                              {ag.active_services && (
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                                  {ag.active_services.split(',').filter(Boolean).map((svc) => (
                                    <span key={svc} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'var(--canvas-subtle)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-mono)', color: 'var(--ink-700)' }}>
                                      {svc.trim()}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td>
                              {ag.is_linked && ag.client_name ? (
                                <span className="badge badge-info">{ag.client_name}</span>
                              ) : (
                                <span className="badge badge-warning">Not linked to a client</span>
                              )}
                            </td>
                            <td className="font-mono text-xs text-muted">
                              {ag.agent_token_prefix ? `${ag.agent_token_prefix}…` : <em>not reported</em>}
                            </td>
                            <td style={{ fontWeight: 700, color: ag.cpu_pct > 80 ? 'var(--brand-coral)' : 'var(--ink-900)' }}>
                              {ag.cpu_pct === null || ag.cpu_pct === undefined ? <em className="text-muted">not reported</em> : `${ag.cpu_pct}%`}
                            </td>
                            <td style={{ fontWeight: 700, color: ag.mem_pct > 85 ? 'var(--brand-coral)' : 'var(--ink-900)' }}>
                              {ag.mem_pct === null || ag.mem_pct === undefined ? <em className="text-muted">not reported</em> : `${ag.mem_pct}%`}
                            </td>
                            <td style={{ fontWeight: 700, color: ag.disk_pct > 90 ? 'var(--brand-coral)' : 'var(--ink-900)' }}>
                              {ag.disk_pct === null || ag.disk_pct === undefined ? <em className="text-muted">not reported</em> : `${ag.disk_pct}%`}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {ag.status === 'unknown' || !age ? (
                                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-coral)' }}>Never reported</span>
                              ) : (
                                <>
                                  <div style={{ fontSize: 12.5, fontWeight: 700, color: ag.status === 'online' ? 'var(--ink-900)' : '#92400E' }}>
                                    {age}
                                  </div>
                                  {ag.last_seen && <div className="font-mono text-xs text-muted">{ag.last_seen}</div>}
                                </>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Confirm discovery / deletion ── */}
      <Modal
        isOpen={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        maxWidth="480px"
        title={confirmAction?.type === 'discover' ? 'Run resource discovery' : 'Delete client'}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirmAction(null)}>Cancel</button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={executeConfirmedAction}
              style={confirmAction?.type === 'delete' ? { background: 'var(--brand-coral)' } : undefined}
            >
              {confirmAction?.type === 'discover' ? 'Run discovery' : 'Delete client'}
            </button>
          </>
        }
      >
        {confirmAction?.type === 'discover' ? (
          <div>
            <p style={{ color: 'var(--ink-900)', fontSize: 13.5, marginBottom: 10 }}>
              Re-enumerate AWS resources for <strong>{confirmAction.clientName}</strong>?
            </p>
            <p style={{ fontSize: 12, color: 'var(--ink-500)', margin: 0 }}>
              This queries EC2, RDS and CloudWatch alarms and replaces the stored inventory. If the collection fails the
              existing inventory is left untouched.
            </p>
          </div>
        ) : (
          <div>
            <p style={{ color: 'var(--brand-coral)', fontSize: 13.5, fontWeight: 700, marginBottom: 8 }}>
              Delete <strong>{confirmAction?.clientName}</strong>?
            </p>
            <p style={{ color: 'var(--ink-500)', fontSize: 12.5, margin: 0 }}>
              Its discovered resources, daily metrics, cost snapshots and SLA snapshots are removed with it. This cannot
              be undone.
            </p>
          </div>
        )}
      </Modal>

      {/* ── Edit schedule ── */}
      <Modal
        isOpen={scheduleTarget !== null}
        onClose={closeSchedule}
        maxWidth="620px"
        title={scheduleTarget ? `Report schedule — ${scheduleTarget.client_name}` : 'Report schedule'}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={closeSchedule}>Cancel</button>
            <button type="submit" form={fieldId('schedule-form')} className="btn btn-primary" disabled={scheduleSaving}>
              {scheduleSaving ? <Loader size={15} aria-hidden="true" className="spin" /> : <Check size={16} aria-hidden="true" />}
              {scheduleSaving ? 'Saving…' : 'Save changes'}
            </button>
          </>
        }
      >
        {scheduleForm && (
          <form id={fieldId('schedule-form')} onSubmit={handleSaveSchedule}>
            {scheduleError && (
              <div role="alert" style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--brand-coral-bg)', border: '1px solid var(--border-ink)', borderRadius: 6, color: '#9A2C14', fontSize: 12.5, fontWeight: 600 }}>
                {scheduleError}
              </div>
            )}

            <div className="form-row">
              <div>
                <label className="form-label" htmlFor={fieldId('sched-time')}>Daily report time (HH:MM, 24-hour)</label>
                <input
                  id={fieldId('sched-time')}
                  type="time"
                  className="form-input"
                  name="report_time"
                  value={scheduleForm.report_time}
                  onChange={handleScheduleChange}
                  aria-invalid={Boolean(scheduleFieldErrors.report_time)}
                  aria-describedby={scheduleFieldErrors.report_time ? fieldId('sched-time-err') : undefined}
                />
                <FieldError id={fieldId('sched-time-err')}>{scheduleFieldErrors.report_time}</FieldError>
              </div>
              <div>
                <label className="form-label" htmlFor={fieldId('sched-region')}>Primary region</label>
                <input
                  id={fieldId('sched-region')}
                  type="text"
                  className="form-input font-mono"
                  name="aws_region"
                  value={scheduleForm.aws_region}
                  onChange={handleScheduleChange}
                  aria-invalid={Boolean(scheduleFieldErrors.aws_region)}
                  aria-describedby={scheduleFieldErrors.aws_region ? fieldId('sched-region-err') : undefined}
                />
                <FieldError id={fieldId('sched-region-err')}>{scheduleFieldErrors.aws_region}</FieldError>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label className="form-label" htmlFor={fieldId('sched-recipients')}>Recipient e-mail addresses</label>
              <input
                id={fieldId('sched-recipients')}
                type="text"
                className="form-input"
                name="recipient_emails"
                placeholder="first.recipient@example.com, second.recipient@example.org"
                value={scheduleForm.recipient_emails}
                onChange={handleScheduleChange}
                aria-invalid={Boolean(scheduleFieldErrors.recipient_emails)}
                aria-describedby={`${fieldId('sched-recipients-hint')}${scheduleFieldErrors.recipient_emails ? ` ${fieldId('sched-recipients-err')}` : ''}`}
              />
              <div id={fieldId('sched-recipients-hint')} style={{ fontSize: 11.5, color: 'var(--ink-500)', marginTop: 4 }}>
                Comma, semicolon or space separated. {validRecipients(scheduleForm.recipient_emails).length} valid address(es) detected.
              </div>
              <FieldError id={fieldId('sched-recipients-err')}>{scheduleFieldErrors.recipient_emails}</FieldError>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label className="form-label" htmlFor={fieldId('sched-assignee')}>Assigned engineer</label>
              <input
                id={fieldId('sched-assignee')}
                type="text"
                className="form-input"
                name="assigned_employee"
                placeholder="Name of the responsible engineer"
                value={scheduleForm.assigned_employee}
                onChange={handleScheduleChange}
              />
            </div>

            <div style={{ ...panelStyle, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <input
                  id={fieldId('sched-enabled')}
                  type="checkbox"
                  name="report_enabled"
                  checked={scheduleForm.report_enabled}
                  onChange={handleScheduleChange}
                  style={{ marginTop: 3 }}
                  aria-describedby={fieldId('sched-enabled-hint')}
                />
                <div>
                  <label className="form-label" htmlFor={fieldId('sched-enabled')} style={{ marginBottom: 2 }}>
                    Send this report automatically every day
                  </label>
                  <div id={fieldId('sched-enabled-hint')} style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>
                    Requires at least one valid recipient. The server also refuses to enable delivery when SMTP is not
                    configured on this deployment, and says so.
                  </div>
                </div>
              </div>
            </div>

            {scheduleTarget?.last_scheduled_run && (
              <p className="text-xs text-muted" style={{ marginTop: 12, marginBottom: 0 }}>
                Last scheduled dispatch: <strong>{scheduleTarget.last_scheduled_run}</strong>
              </p>
            )}
          </form>
        )}
      </Modal>

      {/* ── Onboarding ── */}
      <Modal
        isOpen={showOnboard}
        onClose={closeOnboard}
        maxWidth="720px"
        title={created ? `Onboarded — ${created.client_name}` : 'Onboard a client account'}
        footer={
          created ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={closeOnboard}>Close</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDiscoverForCreated}
                disabled={busyClientId === created.client_id}
              >
                {busyClientId === created.client_id
                  ? <Loader size={15} aria-hidden="true" className="spin" />
                  : <RefreshCw size={15} aria-hidden="true" />}
                {busyClientId === created.client_id ? 'Discovering…' : 'Discover resources'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" onClick={closeOnboard}>Cancel</button>
              <button type="submit" form={fieldId('onboard-form')} className="btn btn-primary" disabled={submitting}>
                {submitting ? <Loader size={15} aria-hidden="true" className="spin" /> : <CheckCircle2 size={16} aria-hidden="true" />}
                {submitting ? 'Creating…' : 'Create client'}
              </button>
            </>
          )
        }
      >
        {created ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div role="status" style={{ ...panelStyle, borderLeft: '5px solid #047857' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <CheckCircle2 size={16} aria-hidden="true" color="#047857" />
                <strong style={{ fontSize: 13, color: 'var(--ink-900)' }}>Client record created</strong>
              </div>
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-700)' }}>{created.next_step}</p>
            </div>

            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', fontSize: 12 }}>
              <dt style={{ fontWeight: 700, color: 'var(--ink-600)' }}>Client ID</dt>
              <dd className="font-mono" style={{ margin: 0 }}>{created.client_id}</dd>
              <dt style={{ fontWeight: 700, color: 'var(--ink-600)' }}>ExternalId</dt>
              <dd className="font-mono" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>{created.external_id || '—'}</span>
                {created.external_id && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ padding: '2px 6px', fontSize: 10 }}
                    onClick={() => copyToClipboard(created.external_id, 'created_extid')}
                  >
                    {copiedKey === 'created_extid' ? <Check size={11} color="#047857" /> : <Copy size={11} />}
                    {copiedKey === 'created_extid' ? 'Copied' : 'Copy'}
                  </button>
                )}
              </dd>
              <dt style={{ fontWeight: 700, color: 'var(--ink-600)' }}>Role ARN</dt>
              <dd className="font-mono" style={{ margin: 0, wordBreak: 'break-all' }}>
                {created.role_arn || 'not applicable for this access mode'}
              </dd>
            </dl>

            <div style={{ ...panelStyle, background: 'var(--canvas-bg)', padding: '12px 14px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-900)', marginBottom: 6 }}>
                Integration &amp; Setup Actions
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {created.client_id && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: 12, padding: '5px 10px' }}
                    onClick={() => {
                      const c = clients.find((x) => x.id === created.client_id) || {
                        id: created.client_id,
                        account_id: formData.account_id,
                        client_name: created.client_name,
                        external_id: created.external_id,
                        role_arn: created.role_arn,
                        aws_region: formData.aws_region,
                        access_type: formData.access_type,
                      }
                      openConnectionModal(c)
                    }}
                  >
                    <Key size={13} aria-hidden="true" style={{ color: '#D97706' }} />
                    View Integration Links &amp; Setup
                  </button>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 6 }}>
                You can revisit and modify these connection settings anytime by clicking the Key icon on the client card.
              </div>
            </div>

            {created.discovery && (
              <Banner tone={created.discovery.errors?.length ? 'warning' : 'success'}>
                <div>
                  Discovery status <strong>{created.discovery.status}</strong>: {created.discovery.discovered ?? 0} resource(s)
                  stored — {created.discovery.ec2_count ?? 0} EC2, {created.discovery.rds_count ?? 0} RDS,{' '}
                  {created.discovery.alarm_count ?? 0} alarm(s).
                  <ErrorList items={created.discovery.errors} />
                </div>
              </Banner>
            )}

            {created.discoveryError && (
              <Banner tone="error">
                <div>
                  {created.discoveryError.message}
                  <ErrorList items={created.discoveryError.errors} />
                </div>
              </Banner>
            )}
          </div>
        ) : (
          <form id={fieldId('onboard-form')} onSubmit={handleSubmitClient}>
            {submitError && (
              <div role="alert" style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--brand-coral-bg)', border: '1px solid var(--border-ink)', borderRadius: 6, color: '#9A2C14', fontSize: 12.5, fontWeight: 600 }}>
                {submitError}
              </div>
            )}

            {/* Step 1 */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <span className="badge badge-info" style={{ fontSize: 10 }}>Step 1</span>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--ink-900)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Account metadata
                </span>
              </div>

              <div className="form-row">
                <div>
                  <label className="form-label" htmlFor={fieldId('client-name')}>Client name *</label>
                  <input
                    id={fieldId('client-name')}
                    type="text"
                    className="form-input"
                    name="client_name"
                    placeholder="Registered name of the client organisation"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    required
                    aria-invalid={Boolean(fieldErrors.client_name)}
                    aria-describedby={fieldErrors.client_name ? fieldId('client-name-err') : undefined}
                  />
                  <FieldError id={fieldId('client-name-err')}>{fieldErrors.client_name}</FieldError>
                </div>
                <div>
                  <label className="form-label" htmlFor={fieldId('account-id')}>AWS account ID (12 digits) *</label>
                  <input
                    id={fieldId('account-id')}
                    type="text"
                    inputMode="numeric"
                    className="form-input font-mono"
                    name="account_id"
                    placeholder="000000000000"
                    value={formData.account_id}
                    onChange={handleInputChange}
                    required
                    aria-invalid={Boolean(fieldErrors.account_id)}
                    aria-describedby={fieldErrors.account_id ? fieldId('account-id-err') : undefined}
                  />
                  <FieldError id={fieldId('account-id-err')}>{fieldErrors.account_id}</FieldError>
                </div>
              </div>

              <div className="form-row">
                <div>
                  <label className="form-label" htmlFor={fieldId('provider')}>Cloud provider</label>
                  <select
                    id={fieldId('provider')}
                    className="form-select"
                    name="cloud_provider"
                    value={formData.cloud_provider}
                    onChange={handleInputChange}
                  >
                    <option value="AWS">Amazon Web Services (AWS)</option>
                    <option value="Azure">Microsoft Azure</option>
                    <option value="GCP">Google Cloud Platform</option>
                  </select>
                </div>
                <div>
                  <label className="form-label" htmlFor={fieldId('region')}>Primary region *</label>
                  <input
                    id={fieldId('region')}
                    type="text"
                    className="form-input font-mono"
                    name="aws_region"
                    placeholder="ap-south-1"
                    value={formData.aws_region}
                    onChange={handleInputChange}
                    required
                    aria-invalid={Boolean(fieldErrors.aws_region)}
                    aria-describedby={fieldErrors.aws_region ? fieldId('region-err') : undefined}
                  />
                  <FieldError id={fieldId('region-err')}>{fieldErrors.aws_region}</FieldError>
                </div>
              </div>

              <div>
                <label className="form-label" htmlFor={fieldId('assignee')}>Assigned engineer</label>
                <input
                  id={fieldId('assignee')}
                  type="text"
                  className="form-input"
                  name="assigned_employee"
                  placeholder="Defaults to you"
                  value={formData.assigned_employee}
                  onChange={handleInputChange}
                  aria-describedby={fieldId('assignee-hint')}
                />
                <div id={fieldId('assignee-hint')} style={{ fontSize: 11.5, color: 'var(--ink-500)', marginTop: 4 }}>
                  Pre-filled with your own name from your session. Leave it as it is unless someone else owns this account.
                </div>
              </div>
            </div>

            <div className="divider" />

            {/* Step 2 */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <span className="badge badge-ok" style={{ fontSize: 10 }}>Step 2</span>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--ink-900)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Access and authentication
                </span>
              </div>

              <fieldset style={{ border: 'none', margin: 0, padding: 0, marginBottom: 14 }}>
                <legend className="form-label" style={{ padding: 0 }}>Access mode</legend>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                  {ACCESS_MODES.map((mode) => {
                    const selected = formData.access_type === mode.id
                    return (
                      <label
                        key={mode.id}
                        htmlFor={fieldId(`mode-${mode.id}`)}
                        style={{
                          display: 'block',
                          padding: '10px 12px',
                          borderRadius: 8,
                          border: selected ? '2px solid var(--brand-blue)' : '1.5px solid var(--border-ink)',
                          background: selected ? 'var(--brand-blue-subtle)' : '#FFFFFF',
                          boxShadow: selected ? 'var(--shadow-solid-blue)' : 'var(--shadow-solid-sm)',
                          cursor: 'pointer',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            id={fieldId(`mode-${mode.id}`)}
                            type="radio"
                            name="access_type"
                            value={mode.id}
                            checked={selected}
                            onChange={handleInputChange}
                          />
                          <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink-900)' }}>{mode.label}</span>
                        </span>
                        <span style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-500)', marginTop: 4 }}>
                          {mode.desc}
                        </span>
                      </label>
                    )
                  })}
                </div>
              </fieldset>

              {formData.access_type === 'iam_role' && (
                <div style={{ ...panelStyle, borderLeft: '5px solid var(--brand-blue)' }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--brand-blue)', marginBottom: 4 }}>
                    Keyless cross-account role assumption
                  </div>
                  <p style={{ margin: '0 0 8px 0', fontSize: 12, color: 'var(--ink-700)', lineHeight: 1.5 }}>
                    The client launches our CloudFormation template to grant read-only CloudWatch access. No static
                    credentials are stored. The ARN and its ExternalId are issued by the server when the record is created.
                  </p>
                  <div className="font-mono" style={{ ...codeBlockStyle, color: 'var(--ink-800)', fontSize: 11 }}>
                    arn:aws:iam::{ACCOUNT_ID_RE.test(formData.account_id.trim()) ? formData.account_id.trim() : 'ACCOUNT_ID'}:role/CloudIQStudioReadRole
                  </div>
                </div>
              )}

              {formData.access_type === 'agent' && (
                <div style={{ ...panelStyle, borderLeft: '5px solid #047857' }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#047857', marginBottom: 4 }}>
                    Linux collector agent
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-700)' }}>
                    An agent token is issued when this client is created. The one-line install command is then available
                    from the collector agents tab, generated by the server so the token and URL are correct for this
                    deployment.
                  </p>
                </div>
              )}

              {formData.access_type === 'api' && (
                <div className="form-row" style={{ marginTop: 10 }}>
                  <div>
                    <label className="form-label" htmlFor={fieldId('access-key')}>IAM access key ID *</label>
                    <input
                      id={fieldId('access-key')}
                      type="text"
                      className="form-input font-mono"
                      name="iam_access_key"
                      autoComplete="off"
                      placeholder="AKIA…"
                      value={formData.iam_access_key}
                      onChange={handleInputChange}
                      aria-invalid={Boolean(fieldErrors.iam_access_key)}
                      aria-describedby={fieldErrors.iam_access_key ? fieldId('access-key-err') : undefined}
                    />
                    <FieldError id={fieldId('access-key-err')}>{fieldErrors.iam_access_key}</FieldError>
                  </div>
                  <div>
                    <label className="form-label" htmlFor={fieldId('secret-key')}>IAM secret access key *</label>
                    <input
                      id={fieldId('secret-key')}
                      type="password"
                      className="form-input font-mono"
                      name="iam_secret_key"
                      autoComplete="off"
                      value={formData.iam_secret_key}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}

              {formData.access_type === 'console_ui' && (
                <div className="form-row" style={{ marginTop: 10 }}>
                  <div>
                    <label className="form-label" htmlFor={fieldId('console-url')}>Console sign-in URL</label>
                    <input
                      id={fieldId('console-url')}
                      type="url"
                      className="form-input font-mono"
                      name="console_url"
                      placeholder="https://signin.aws.amazon.com/console"
                      value={formData.console_url}
                      onChange={handleInputChange}
                      aria-invalid={Boolean(fieldErrors.console_url)}
                      aria-describedby={`${fieldId('console-url-hint')}${fieldErrors.console_url ? ` ${fieldId('console-url-err')}` : ''}`}
                    />
                    <div id={fieldId('console-url-hint')} style={{ fontSize: 11.5, color: 'var(--ink-500)', marginTop: 4 }}>
                      HTTPS only, on an AWS, Azure or Google Cloud console domain.
                    </div>
                    <FieldError id={fieldId('console-url-err')}>{fieldErrors.console_url}</FieldError>
                  </div>
                  <div>
                    <label className="form-label" htmlFor={fieldId('iam-username')}>IAM user name</label>
                    <input
                      id={fieldId('iam-username')}
                      type="text"
                      className="form-input"
                      name="iam_username"
                      placeholder="Read-only IAM user"
                      value={formData.iam_username}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}

              {(formData.access_type === 'iam_role' || formData.access_type === 'api') && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                    <button type="button" className="btn btn-secondary" onClick={handleTestConnection} disabled={testing}>
                      {testing ? <Loader size={14} aria-hidden="true" className="spin" /> : <Zap size={14} aria-hidden="true" />}
                      {testing ? 'Testing…' : 'Test connection'}
                    </button>
                    <span style={{ fontSize: 11.5, color: 'var(--ink-500)' }}>
                      Confirms which AWS account these credentials actually belong to, before anything is stored.
                    </span>
                  </div>
                  {testError && (
                    <div role="alert" style={{ fontSize: 12.5, fontWeight: 600, color: '#9A2C14', marginBottom: 10 }}>
                      {testError}
                    </div>
                  )}
                  {testResult && (
                    <div>
                      <ConnectionResult result={testResult} />
                      {testResult.caller_identity?.account
                        && ACCOUNT_ID_RE.test(formData.account_id.trim())
                        && testResult.caller_identity.account !== formData.account_id.trim() && (
                        <div role="alert" style={{ marginTop: 10, padding: '10px 14px', background: 'var(--brand-coral-bg)', border: '1.5px solid var(--border-ink)', borderRadius: 6, color: '#9A2C14', fontSize: 12.5, fontWeight: 700 }}>
                          These credentials belong to account {testResult.caller_identity.account}, not the{' '}
                          {formData.account_id.trim()} you entered. Fix one of the two before creating the client.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="divider" />

            {/* Step 3 */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <span className="badge badge-warning" style={{ fontSize: 10 }}>Step 3</span>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--ink-900)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Daily report delivery
                </span>
              </div>

              <div className="form-row">
                <div>
                  <label className="form-label" htmlFor={fieldId('recipients')}>Recipient e-mail addresses</label>
                  <input
                    id={fieldId('recipients')}
                    type="text"
                    className="form-input"
                    name="recipient_emails"
                    placeholder="first.recipient@example.com, second.recipient@example.org"
                    value={formData.recipient_emails}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(fieldErrors.recipient_emails)}
                    aria-describedby={`${fieldId('recipients-hint')}${fieldErrors.recipient_emails ? ` ${fieldId('recipients-err')}` : ''}`}
                  />
                  <div id={fieldId('recipients-hint')} style={{ fontSize: 11.5, color: 'var(--ink-500)', marginTop: 4 }}>
                    Comma, semicolon or space separated. {validRecipients(formData.recipient_emails).length} valid address(es) detected.
                  </div>
                  <FieldError id={fieldId('recipients-err')}>{fieldErrors.recipient_emails}</FieldError>
                </div>
                <div>
                  <label className="form-label" htmlFor={fieldId('report-time')}>Report time (HH:MM, 24-hour)</label>
                  <input
                    id={fieldId('report-time')}
                    type="time"
                    className="form-input"
                    name="report_time"
                    value={formData.report_time}
                    onChange={handleInputChange}
                    aria-invalid={Boolean(fieldErrors.report_time)}
                    aria-describedby={fieldErrors.report_time ? fieldId('report-time-err') : undefined}
                  />
                  <FieldError id={fieldId('report-time-err')}>{fieldErrors.report_time}</FieldError>
                </div>
              </div>

              <div style={{ ...panelStyle, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <input
                    id={fieldId('report-enabled')}
                    type="checkbox"
                    name="report_enabled"
                    checked={formData.report_enabled}
                    onChange={handleInputChange}
                    style={{ marginTop: 3 }}
                    aria-describedby={fieldId('report-enabled-hint')}
                  />
                  <div>
                    <label className="form-label" htmlFor={fieldId('report-enabled')} style={{ marginBottom: 2 }}>
                      Send this report automatically every day
                    </label>
                    <div id={fieldId('report-enabled-hint')} style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>
                      Enabling scheduled delivery requires at least one valid recipient address. If SMTP is not configured
                      on this deployment the server refuses to enable it and explains what is missing.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Modal>

      {/* ── Connection & Integration Setup ── */}
      <Modal
        isOpen={connectionTarget !== null}
        onClose={closeConnectionModal}
        maxWidth="760px"
        title={
          connectionTarget
            ? `Connection & Integration — ${connectionTarget.client_name}`
            : 'Connection & Integration'
        }
        footer={
          connectionTab === 'edit' ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={closeConnectionModal}>
                Cancel
              </button>
              <button
                type="submit"
                form={fieldId('connection-edit-form')}
                className="btn btn-primary"
                disabled={connectionSaving}
              >
                {connectionSaving ? (
                  <Loader size={15} aria-hidden="true" className="spin" />
                ) : (
                  <Check size={16} aria-hidden="true" />
                )}
                {connectionSaving ? 'Saving changes…' : 'Save connection changes'}
              </button>
            </>
          ) : connectionTab === 'whitelist' ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={closeConnectionModal}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveWhitelistOnly}
                disabled={whitelistSaving}
              >
                {whitelistSaving ? (
                  <Loader size={15} aria-hidden="true" className="spin" />
                ) : (
                  <Check size={16} aria-hidden="true" />
                )}
                {whitelistSaving ? 'Saving whitelist…' : 'Save IP Whitelist'}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-primary" onClick={closeConnectionModal}>
              Close
            </button>
          )
        }
      >
        {connectionTarget && (
          <div>
            {/* Modal Subtabs */}
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 18,
                borderBottom: '1.5px solid var(--border-ink)',
                paddingBottom: 10,
              }}
            >
              <button
                type="button"
                className={`btn ${connectionTab === 'links' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ padding: '6px 14px', fontSize: 12.5 }}
                onClick={() => setConnectionTab('links')}
              >
                <FileText size={14} aria-hidden="true" /> Integration Links &amp; Setup
              </button>
              <button
                type="button"
                className={`btn ${connectionTab === 'edit' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ padding: '6px 14px', fontSize: 12.5 }}
                onClick={() => setConnectionTab('edit')}
              >
                <Sliders size={14} aria-hidden="true" /> Edit Connection &amp; Credentials
              </button>
              <button
                type="button"
                className={`btn ${connectionTab === 'whitelist' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ padding: '6px 14px', fontSize: 12.5 }}
                onClick={() => setConnectionTab('whitelist')}
              >
                <Shield size={14} aria-hidden="true" /> Observability Access &amp; IP Whitelist
              </button>
            </div>

            {/* TAB 1: INTEGRATION LINKS & SETUP */}
            {connectionTab === 'links' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* 1. Account Identity & External ID */}
                <div style={{ ...panelStyle, background: 'var(--canvas-bg)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                        Account &amp; Security External ID
                      </div>
                      <div className="font-mono" style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-900)', marginTop: 3 }}>
                        AWS Account: <strong>{connectionTarget.account_id}</strong> · Region:{' '}
                        <strong>{connectionTarget.aws_region || 'ap-south-1'}</strong>
                      </div>
                    </div>
                    {connectionTarget.external_id && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="font-mono text-xs" style={{ background: '#FFF', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border-ink)' }}>
                          ExternalId: {connectionTarget.external_id}
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ padding: '4px 8px', fontSize: 11 }}
                          onClick={() => copyToClipboard(connectionTarget.external_id, 'extid')}
                        >
                          {copiedKey === 'extid' ? <Check size={12} color="#047857" /> : <Copy size={12} />}
                          {copiedKey === 'extid' ? 'Copied' : 'Copy ID'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* 2. 1-Click AWS CloudFormation */}
                <div style={{ ...panelStyle, borderLeft: '5px solid var(--brand-blue)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--brand-blue)' }}>
                      Option A: 1-Click AWS CloudFormation (IAM Role Delegation)
                    </div>
                    <span className="badge badge-ok" style={{ fontSize: 10 }}>Recommended · Zero Stored Keys</span>
                  </div>
                  <p style={{ margin: '0 0 12px 0', fontSize: 12, color: 'var(--ink-700)', lineHeight: 1.5 }}>
                    Deploys the read-only role <code>CloudIQStudioReadRole</code> in the client AWS account (<code>{connectionTarget.account_id}</code>).
                    The trust policy grants CloudIQ Studio secure read-only permission using the client&apos;s unique ExternalId.
                  </p>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ fontSize: 12, padding: '6px 12px' }}
                      onClick={() => handleDownloadCfYaml(connectionTarget, cfTemplateText)}
                      disabled={cfTemplateLoading || !cfTemplateText}
                    >
                      <Download size={13} aria-hidden="true" />
                      Download YAML Template
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ fontSize: 12, padding: '6px 12px' }}
                      onClick={() => copyToClipboard(cfTemplateText, 'yaml')}
                      disabled={cfTemplateLoading || !cfTemplateText}
                    >
                      {copiedKey === 'yaml' ? <Check size={13} color="#047857" /> : <Copy size={13} />}
                      {copiedKey === 'yaml' ? 'Template Copied!' : 'Copy YAML Code'}
                    </button>
                    <a
                      href={`https://${connectionTarget.aws_region || 'ap-south-1'}.console.aws.amazon.com/cloudformation/home?region=${connectionTarget.aws_region || 'ap-south-1'}#/stacks/create/template`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                      style={{ fontSize: 12, padding: '6px 12px', textDecoration: 'none' }}
                    >
                      <ExternalLink size={13} aria-hidden="true" /> Open AWS Console
                    </a>
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={() => setShowCfYamlPreview((prev) => !prev)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        fontSize: 11.5,
                        color: 'var(--brand-blue)',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      {showCfYamlPreview ? 'Hide template YAML source' : 'View template YAML source'}
                    </button>
                    {showCfYamlPreview && (
                      <pre
                        className="font-mono"
                        style={{
                          ...codeBlockStyle,
                          maxHeight: 220,
                          overflowY: 'auto',
                          fontSize: 11,
                          marginTop: 8,
                          background: '#1E293B',
                          color: '#F8FAFC',
                          padding: '10px 14px',
                          borderRadius: 6,
                        }}
                      >
                        {cfTemplateLoading ? 'Loading template…' : cfTemplateText || 'No template available.'}
                      </pre>
                    )}
                  </div>
                </div>

                {/* 3. CloudPulse Linux Agent */}
                <div style={{ ...panelStyle, borderLeft: '5px solid #047857' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#047857' }}>
                      Option B: CloudPulse Agent (In-Guest Telemetry &amp; Logs)
                    </div>
                    <span className="badge badge-info" style={{ fontSize: 10 }}>Zero Synthetic Data</span>
                  </div>
                  <p style={{ margin: '0 0 10px 0', fontSize: 12, color: 'var(--ink-700)', lineHeight: 1.5 }}>
                    Run this command on any monitored Ubuntu, Debian, RHEL or CentOS host. The node will automatically stream real-time CPU, RAM, disk, and application log events into CloudPulse:
                  </p>

                  <div style={{ position: 'relative' }}>
                    <pre
                      className="font-mono"
                      style={{
                        ...codeBlockStyle,
                        background: '#0F172A',
                        color: '#38BDF8',
                        padding: '12px 14px',
                        paddingRight: 80,
                        borderRadius: 6,
                        fontSize: 11.5,
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        margin: 0,
                      }}
                    >
                      {targetPulseLoading ? 'Loading install command…' : targetPulseCmd || 'Generating command…'}
                    </pre>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        padding: '4px 10px',
                        fontSize: 11,
                        background: '#1E293B',
                        color: '#FFF',
                        borderColor: '#334155',
                      }}
                      onClick={() => copyToClipboard(targetPulseCmd, 'pulse_cmd')}
                      disabled={!targetPulseCmd}
                    >
                      {copiedKey === 'pulse_cmd' ? <Check size={12} color="#34D399" /> : <Copy size={12} />}
                      {copiedKey === 'pulse_cmd' ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: EDIT CONNECTION & CREDENTIALS */}
            {connectionTab === 'edit' && connectionForm && (
              <form id={fieldId('connection-edit-form')} onSubmit={handleSaveConnection}>
                {connectionError && (
                  <div
                    role="alert"
                    style={{
                      marginBottom: 14,
                      padding: '10px 14px',
                      background: 'var(--brand-coral-bg)',
                      border: '1px solid var(--border-ink)',
                      borderRadius: 6,
                      color: '#9A2C14',
                      fontSize: 12.5,
                      fontWeight: 600,
                    }}
                  >
                    {connectionError}
                  </div>
                )}

                <div className="form-row" style={{ marginBottom: 14 }}>
                  <div>
                    <label className="form-label" htmlFor={fieldId('edit-client-name')}>
                      Client Name *
                    </label>
                    <input
                      id={fieldId('edit-client-name')}
                      type="text"
                      className="form-input"
                      name="client_name"
                      value={connectionForm.client_name}
                      onChange={handleConnectionFormChange}
                      required
                    />
                    <FieldError id={fieldId('edit-name-err')}>{connectionFieldErrors.client_name}</FieldError>
                  </div>
                  <div>
                    <label className="form-label" htmlFor={fieldId('edit-region')}>
                      Primary AWS Region
                    </label>
                    <input
                      id={fieldId('edit-region')}
                      type="text"
                      className="form-input font-mono"
                      name="aws_region"
                      value={connectionForm.aws_region}
                      onChange={handleConnectionFormChange}
                      placeholder="ap-south-1"
                    />
                    <FieldError id={fieldId('edit-region-err')}>{connectionFieldErrors.aws_region}</FieldError>
                  </div>
                </div>

                {/* Access Mode Selector */}
                <fieldset style={{ border: 'none', margin: 0, padding: 0, marginBottom: 16 }}>
                  <legend className="form-label" style={{ padding: 0 }}>
                    Access Mode
                  </legend>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                    {ACCESS_MODES.map((mode) => {
                      const selected = connectionForm.access_type === mode.id
                      return (
                        <label
                          key={mode.id}
                          htmlFor={fieldId(`edit-mode-${mode.id}`)}
                          style={{
                            display: 'block',
                            padding: '10px 12px',
                            borderRadius: 8,
                            border: selected ? '2px solid var(--brand-blue)' : '1.5px solid var(--border-ink)',
                            background: selected ? 'var(--brand-blue-subtle)' : '#FFFFFF',
                            boxShadow: selected ? 'var(--shadow-solid-blue)' : 'var(--shadow-solid-sm)',
                            cursor: 'pointer',
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                              id={fieldId(`edit-mode-${mode.id}`)}
                              type="radio"
                              name="access_type"
                              value={mode.id}
                              checked={selected}
                              onChange={handleConnectionFormChange}
                            />
                            <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink-900)' }}>{mode.label}</span>
                          </span>
                          <span style={{ display: 'block', fontSize: 10.5, color: 'var(--ink-500)', marginTop: 4 }}>
                            {mode.desc}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </fieldset>

                {/* Mode: IAM Role */}
                {connectionForm.access_type === 'iam_role' && (
                  <div style={{ ...panelStyle, marginBottom: 14 }}>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label className="form-label" htmlFor={fieldId('edit-role-arn')}>
                          IAM Role ARN
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setConnectionForm((prev) => ({
                              ...prev,
                              role_arn: `arn:aws:iam::${connectionTarget.account_id}:role/CloudIQStudioReadRole`,
                            }))
                          }
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            fontSize: 11,
                            color: 'var(--brand-blue)',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                        >
                          Reset to standard ARN
                        </button>
                      </div>
                      <input
                        id={fieldId('edit-role-arn')}
                        type="text"
                        className="form-input font-mono"
                        name="role_arn"
                        value={connectionForm.role_arn}
                        onChange={handleConnectionFormChange}
                        placeholder={`arn:aws:iam::${connectionTarget.account_id}:role/CloudIQStudioReadRole`}
                      />
                      <FieldError id={fieldId('edit-role-err')}>{connectionFieldErrors.role_arn}</FieldError>
                    </div>

                    <div>
                      <label className="form-label" htmlFor={fieldId('edit-external-id')}>
                        External ID
                      </label>
                      <input
                        id={fieldId('edit-external-id')}
                        type="text"
                        className="form-input font-mono"
                        name="external_id"
                        value={connectionForm.external_id}
                        onChange={handleConnectionFormChange}
                        placeholder={connectionTarget.external_id || 'cliq-ext-...'}
                      />
                      <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 4 }}>
                        Must match the ExternalId parameter deployed in the client’s CloudFormation stack.
                      </div>
                    </div>
                  </div>
                )}

                {/* Mode: API Keys */}
                {connectionForm.access_type === 'api' && (
                  <div style={{ ...panelStyle, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <Shield size={15} color="var(--brand-teal)" />
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-900)' }}>
                        Vault Credentials Status:{' '}
                        {connectionTarget.has_iam_access_key && connectionTarget.has_iam_secret_key
                          ? 'Active key pair held in AES-GCM vault'
                          : 'No key pair stored'}
                      </span>
                    </div>

                    <div className="form-row">
                      <div>
                        <label className="form-label" htmlFor={fieldId('edit-access-key')}>
                          New IAM Access Key ID
                        </label>
                        <input
                          id={fieldId('edit-access-key')}
                          type="text"
                          className="form-input font-mono"
                          name="iam_access_key"
                          autoComplete="off"
                          placeholder={
                            connectionTarget.has_iam_access_key
                              ? '•••••••••••••••••••• (leave blank to keep current)'
                              : 'AKIA…'
                          }
                          value={connectionForm.iam_access_key}
                          onChange={handleConnectionFormChange}
                        />
                        <FieldError id={fieldId('edit-ak-err')}>{connectionFieldErrors.iam_access_key}</FieldError>
                      </div>

                      <div>
                        <label className="form-label" htmlFor={fieldId('edit-secret-key')}>
                          New IAM Secret Access Key
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            id={fieldId('edit-secret-key')}
                            type={showSecretKey ? 'text' : 'password'}
                            className="form-input font-mono"
                            name="iam_secret_key"
                            autoComplete="off"
                            placeholder={
                              connectionTarget.has_iam_secret_key
                                ? '•••••••••••••••••••• (leave blank to keep current)'
                                : 'Secret Key'
                            }
                            value={connectionForm.iam_secret_key}
                            onChange={handleConnectionFormChange}
                            style={{ paddingRight: 36 }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowSecretKey((p) => !p)}
                            style={{
                              position: 'absolute',
                              right: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'var(--ink-500)',
                            }}
                          >
                            {showSecretKey ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 6 }}>
                      Leave blank to preserve existing vault credentials, or enter both keys to rotate.
                    </div>
                  </div>
                )}

                {/* Pre-flight Connection Verification */}
                <div style={{ ...panelStyle, background: 'var(--canvas-bg)', marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-900)' }}>
                        Pre-flight Connection Verification
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>
                        Test cross-account role assumption or IAM keys before saving.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleTestConnectionInModal}
                      disabled={testConnLoading}
                    >
                      {testConnLoading ? (
                        <Loader size={13} aria-hidden="true" className="spin" />
                      ) : (
                        <Zap size={13} aria-hidden="true" />
                      )}
                      {testConnLoading ? 'Verifying AWS access…' : 'Test AWS Connection'}
                    </button>
                  </div>

                  {testConnError && (
                    <div
                      role="alert"
                      style={{
                        marginTop: 10,
                        padding: '8px 12px',
                        background: 'var(--brand-coral-bg)',
                        borderRadius: 6,
                        color: '#9A2C14',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {testConnError}
                    </div>
                  )}

                  {testConnResult && (
                    <div style={{ marginTop: 10 }}>
                      <ConnectionResult result={testConnResult} />
                    </div>
                  )}
                </div>
              </form>
            )}

            {/* TAB 3: OBSERVABILITY ACCESS & IP WHITELIST */}
            {connectionTab === 'whitelist' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* 1. Header & Live Dashboard URL Banner */}
                <div style={{ ...panelStyle, background: 'var(--canvas-bg)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                        Client Observability Portal
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink-900)', marginTop: 2 }}>
                        Zero-Password Seamless Access via IP Whitelisting
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 4, maxWidth: 540 }}>
                        Authorized client office IP addresses gain immediate Viewer access to the white-labeled Grafana observability dashboard. All unauthorized public IP addresses are blocked with 403 Forbidden.
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <a
                        href="https://cloudpulse.adamastech.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                        style={{ fontSize: 12, padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <ExternalLink size={13} aria-hidden="true" /> Open Portal
                      </a>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ fontSize: 12, padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        onClick={() => {
                          navigator.clipboard.writeText('https://cloudpulse.adamastech.in')
                          setCopiedDashboardUrl(true)
                          setTimeout(() => setCopiedDashboardUrl(false), 2000)
                        }}
                      >
                        {copiedDashboardUrl ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
                        {copiedDashboardUrl ? 'Copied' : 'Copy URL'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* 2. Whitelist Input & Auto-Detect */}
                <div style={panelStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label
                      htmlFor="client-whitelist-ips"
                      style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-800)', textTransform: 'uppercase' }}
                    >
                      Allowed Client Office IP Addresses &amp; CIDRs
                    </label>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ fontSize: 11.5, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                      onClick={handleAddMyIp}
                      disabled={detectingIp}
                    >
                      {detectingIp ? <Loader size={12} className="spin" /> : <Plus size={12} />}
                      {detectingIp ? 'Detecting IP…' : 'Add My Current IP'}
                    </button>
                  </div>

                  <textarea
                    id="client-whitelist-ips"
                    rows={4}
                    className="font-mono text-xs"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 6,
                      border: '1px solid var(--border-ink)',
                      background: '#FFF',
                      lineHeight: 1.6,
                    }}
                    placeholder="e.g. 182.74.56.21, 103.21.45.0/24 (comma or newline separated)"
                    value={connectionForm.allowed_dashboard_ips || ''}
                    onChange={(e) =>
                      setConnectionForm((prev) => ({ ...prev, allowed_dashboard_ips: e.target.value }))
                    }
                  />
                  <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 6 }}>
                    Enter client corporate static IPs or office CIDR subnets. When staff access <strong>https://cloudpulse.adamastech.in</strong> from these IPs, Grafana grants seamless Viewer access without requiring credentials.
                  </div>

                  {whitelistError && (
                    <div
                      role="alert"
                      style={{
                        marginTop: 10,
                        padding: '8px 12px',
                        background: 'var(--brand-coral-bg)',
                        borderRadius: 6,
                        color: '#9A2C14',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {whitelistError}
                    </div>
                  )}

                  {whitelistSuccess && (
                    <div
                      role="status"
                      style={{
                        marginTop: 10,
                        padding: '8px 12px',
                        background: 'var(--brand-teal-bg, #ECFDF5)',
                        borderRadius: 6,
                        color: '#065F46',
                        fontSize: 12,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <CheckCircle2 size={14} aria-hidden="true" />
                      {whitelistSuccess}
                    </div>
                  )}

                  <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSaveWhitelistOnly}
                      disabled={whitelistSaving}
                      style={{ fontSize: 12.5 }}
                    >
                      {whitelistSaving ? (
                        <Loader size={14} aria-hidden="true" className="spin" />
                      ) : (
                        <Check size={14} aria-hidden="true" />
                      )}
                      {whitelistSaving ? 'Saving whitelist…' : 'Save IP Whitelist'}
                    </button>

                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleLoadNginxSnippet}
                      disabled={loadingNginxSnippet}
                      style={{ fontSize: 12.5 }}
                    >
                      {loadingNginxSnippet ? (
                        <Loader size={14} aria-hidden="true" className="spin" />
                      ) : (
                        <Terminal size={14} aria-hidden="true" />
                      )}
                      {nginxSnippetPreview ? 'Hide Nginx Rules' : 'Preview Nginx Rules'}
                    </button>
                  </div>
                </div>

                {/* 3. Nginx Snippet Preview (Collapsible) */}
                {nginxSnippetPreview && (
                  <div style={panelStyle}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase', marginBottom: 6 }}>
                      Nginx Snippet Preview (/etc/nginx/snippets/client-whitelist.conf)
                    </div>
                    <pre
                      className="font-mono text-xs"
                      style={{
                        margin: 0,
                        padding: 12,
                        background: '#1E293B',
                        color: '#E2E8F0',
                        borderRadius: 6,
                        maxHeight: 180,
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {nginxSnippetPreview}
                    </pre>
                  </div>
                )}

                {/* 4. Automated Pulse Server Sync Instruction */}
                <div style={{ ...panelStyle, background: 'var(--canvas-bg)' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase' }}>
                    Pulse Core Server Synchronization
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 4 }}>
                    Server 2 (16.4.61.1) automatically pulls updated IP whitelists from CloudIQ Studio every 60 seconds via cron:
                  </div>
                  <pre
                    className="font-mono text-xs"
                    style={{
                      margin: '8px 0 0',
                      padding: '8px 10px',
                      background: '#FFF',
                      border: '1px solid var(--border-ink)',
                      borderRadius: 4,
                      color: 'var(--ink-900)',
                      whiteSpace: 'pre-wrap',
                      overflowX: 'auto',
                    }}
                  >
                    curl -fsSL https://cloudiq.adamastech.in/api/pulse/nginx-whitelist -o /etc/nginx/snippets/client-whitelist.conf &amp;&amp; nginx -t &amp;&amp; systemctl reload nginx
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
